/**
 * MarketingOverview — Live overview wired to real CRM data.
 *
 * Mock seed arrays (upcoming/topCampaigns/pipelineCounts) have been removed.
 * Until backend `/api/marketing/*` endpoints are wired up, the page renders
 * empty states. Audience tile pulls from real `/api/leads` + `/api/contacts`.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import {
  Mail, Users, FileText, Eye, TrendingUp, ArrowRight, Loader2, PenSquare,
} from "lucide-react";
import MarketingShell, { MarketingAIBanner } from "@/components/MarketingShell";

const PIPELINE_STAGES = [
  { key: "idea", label: "Ideas", color: "var(--gray)" },
  { key: "draft", label: "Drafts", color: "var(--amber)" },
  { key: "in_review", label: "In Review", color: "var(--blue)" },
  { key: "approved", label: "Approved", color: "var(--green)" },
  { key: "published", label: "Published", color: "var(--purple)" },
];

export default function MarketingOverview() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(true);
  const [stats, setStats] = useState({ campaigns: 0, activeCampaigns: 0, audience: 0, openRate: 0, contentPipeline: 0 });
  const [upcoming] = useState([]);
  const [topCampaigns] = useState([]);
  const [pipelineCounts] = useState({ idea: 0, draft: 0, in_review: 0, approved: 0, published: 0 });

  const loadData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [leadsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts`, { headers }).catch(() => ({ data: [] })),
      ]);
      const totalAudience = (leadsRes.data?.length || 0) + (contactsRes.data?.length || 0);
      setStats((s) => ({ ...s, audience: totalAudience }));

      try {
        const aiRes = await axios.post(`${API}/ai/dashboard-briefing`, {}, { headers });
        setAiInsight(aiRes.data?.briefing || "Connect a channel and create your first campaign to see AI-driven insights here.");
      } catch {
        setAiInsight("Connect a channel and create your first campaign to see AI-driven insights here.");
      }
    } catch {
      setAiInsight("Welcome to your Marketing Hub. Create campaigns, manage content, and reach your audience.");
    } finally {
      setLoading(false);
      setAiLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const headerActions = (
    <>
      <button className="btn" onClick={() => navigate("/marketing/create/studio")} data-testid="create-post-btn">
        <PenSquare style={{ width: 13, height: 13 }} /> Create Post
      </button>
      <button className="btn btn-primary" onClick={() => navigate("/marketing/plan/campaigns")} data-testid="new-campaign-btn">
        <Mail style={{ width: 13, height: 13 }} /> New Campaign
      </button>
    </>
  );

  if (loading) {
    return (
      <MarketingShell title="Marketing Overview" subtitle="Loading…">
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }} data-testid="marketing-overview-loading">
          <Loader2 style={{ width: 24, height: 24, animation: "spin 1s linear infinite", color: "var(--blue)" }} />
        </div>
      </MarketingShell>
    );
  }

  const kpis = [
    { label: "Total campaigns", value: stats.campaigns, icon: Mail },
    { label: "Active campaigns", value: stats.activeCampaigns, icon: TrendingUp },
    { label: "Total audience", value: stats.audience.toLocaleString(), icon: Users },
    { label: "Avg open rate", value: stats.openRate ? `${stats.openRate}%` : "—", icon: Eye },
    { label: "Content pipeline", value: stats.contentPipeline, icon: FileText },
  ];

  const EmptyHint = ({ children, testid }) => (
    <div
      data-testid={testid}
      style={{
        padding: "28px 18px", textAlign: "center", color: "var(--text-tertiary)",
        fontSize: 12.5, lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );

  return (
    <MarketingShell title="Marketing Overview" subtitle="Campaigns, content, and audience at a glance" actions={headerActions}>
      <div data-testid="marketing-overview">
        <MarketingAIBanner loading={aiLoading}>
          {aiLoading ? "Analyzing your marketing data…" : aiInsight}
        </MarketingAIBanner>

        {/* KPI Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 22 }}>
          {kpis.map((kpi) => (
            <div key={kpi.label} className="mkt-kpi" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="mkt-kpi-icon"><kpi.icon style={{ width: 14, height: 14 }} /></div>
              <div className="mkt-kpi-value">{kpi.value}</div>
              <div className="mkt-kpi-label">{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* Two Column: Upcoming + Top Campaigns */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 22 }}>
          <div className="mkt-card mkt-card-pad">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 className="card-title">Upcoming this week</h3>
              <button className="btn" style={{ height: 26, padding: "0 10px", fontSize: 11 }}
                onClick={() => navigate("/marketing/plan/calendar")} data-testid="view-calendar-btn">
                View calendar <ArrowRight style={{ width: 12, height: 12 }} />
              </button>
            </div>
            {upcoming.length === 0 ? (
              <EmptyHint testid="upcoming-empty">
                No scheduled content this week.<br />
                Schedule a post from the Calendar or Content Studio to populate this list.
              </EmptyHint>
            ) : null}
          </div>

          <div className="mkt-card mkt-card-pad">
            <h3 className="card-title" style={{ marginBottom: 14 }}>Top performing campaigns</h3>
            {topCampaigns.length === 0 ? (
              <EmptyHint testid="top-campaigns-empty">
                No campaign performance data yet.<br />
                Send your first campaign to see open + click rates here.
              </EmptyHint>
            ) : null}
          </div>
        </div>

        {/* Content Pipeline */}
        <div className="mkt-card mkt-card-pad">
          <h3 className="card-title" style={{ marginBottom: 14 }}>Content pipeline</h3>
          <div style={{ display: "flex", gap: 10 }}>
            {PIPELINE_STAGES.map((stage, i) => (
              <button
                key={stage.key}
                onClick={() => navigate("/marketing/create/studio")}
                className="mkt-card is-interactive"
                style={{ flex: 1, padding: "14px 12px", background: "var(--paper)", cursor: "pointer", textAlign: "center", position: "relative", border: "0.5px solid var(--border)" }}
                data-testid={`pipeline-stage-${stage.key}`}
              >
                <div className="num" style={{ fontSize: 22, fontWeight: 650, color: stage.color, lineHeight: 1 }}>{pipelineCounts[stage.key]}</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 6 }}>{stage.label}</div>
                {i < PIPELINE_STAGES.length - 1 && (
                  <ArrowRight style={{ position: "absolute", right: -14, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--text-tertiary)", zIndex: 1 }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </MarketingShell>
  );
}
